/**
 * RevenueCat In-App Purchase wrapper (iOS + Android).
 *
 * This is the ONLY place in the app that talks to
 * @revenuecat/purchases-capacitor. Every other module (Paywall, Signup,
 * RequireSubscription) goes through the small surface exported here:
 *
 *   initIap(userId)         Configure the SDK and log the current user in.
 *                           Called once per app session, after Supabase auth.
 *   getOfferings()          Fetch the "current" offering from RevenueCat.
 *                           Returns the packages configured in RC dashboard.
 *   purchasePackage(pkg)    Kick off the Apple/Google purchase sheet.
 *                           Returns the resulting CustomerInfo on success.
 *   restorePurchases()      Apple Guideline 3.1.1 — every subscription app
 *                           MUST expose a "Restore purchases" control.
 *   hasProEntitlement(ci)   Cheap client-side check for the "pro" entitlement
 *                           (defined in the RC dashboard). Server-side truth
 *                           still lives in `organisations.subscription_status`
 *                           written by the revenuecat-webhook Edge Function.
 *   logoutIap()             Clear the RC session on sign-out.
 *
 * WHY WE CHOSE REVENUECAT:
 *   Raw StoreKit + Google Play Billing gets us in the door but leaves us
 *   maintaining receipt validation, subscription-status tracking, cross-
 *   platform account linking, and analytics ourselves. RevenueCat wraps
 *   all of that in a single SDK + webhook for £0 up to $2.5k/month MTR.
 *   1 week of eng vs 2–4 weeks. See notes in /3.0 App/decision-log.md.
 *
 * WEB / PWA:
 *   All functions no-op on the web. Web subscribers go through Stripe
 *   Checkout via lib/billing.ts — nothing here runs.
 */

import { isNativeApp, nativePlatform } from './nativeShell';

// The RevenueCat SDK is dynamically imported the first time we actually
// need it. This keeps the web bundle free of the native-only code paths
// and avoids "Purchases is not available in the browser" console noise
// when the PWA boots.
type PurchasesPlugin = typeof import('@revenuecat/purchases-capacitor')['Purchases'];
type LOG_LEVEL_TYPE = typeof import('@revenuecat/purchases-capacitor')['LOG_LEVEL'];
type CustomerInfo = import('@revenuecat/purchases-capacitor').CustomerInfo;
type PurchasesPackage = import('@revenuecat/purchases-capacitor').PurchasesPackage;
type PurchasesOffering = import('@revenuecat/purchases-capacitor').PurchasesOffering;

let purchasesModule: { Purchases: PurchasesPlugin; LOG_LEVEL: LOG_LEVEL_TYPE } | null = null;
async function loadPurchases() {
  if (purchasesModule) return purchasesModule;
  // Vite tree-shakes this import out of the web bundle because we gate
  // the caller with isNativeApp() first.
  purchasesModule = await import('@revenuecat/purchases-capacitor');
  return purchasesModule;
}

// The RC "public" SDK keys. These are safe to ship in the client bundle
// (they're the SDK-side keys — the secret-side key never leaves the
// server). We inject them at build time from Netlify env so we don't
// hard-code them into git.
const IOS_KEY = import.meta.env.VITE_REVENUECAT_IOS_API_KEY as string | undefined;
const ANDROID_KEY = import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY as string | undefined;

// Guard so we only configure the SDK once per app session.
let configured = false;
let currentUserId: string | null = null;

/**
 * Configure RevenueCat and log the current Supabase user in. Idempotent —
 * safe to call on every sign-in / app-resume.
 *
 * We use the Supabase user id as the RevenueCat `app_user_id`. That's how
 * the revenuecat-webhook Edge Function maps incoming events back to the
 * right organisation row in our database (see webhook step 3).
 */
export async function initIap(userId: string): Promise<void> {
  if (!isNativeApp()) return;
  if (!userId) return;

  const platform = nativePlatform();
  const apiKey = platform === 'ios' ? IOS_KEY : ANDROID_KEY;
  if (!apiKey) {
    // In dev builds the env vars may not be set yet — log once and stay
    // no-op rather than crashing the whole app boot.
    console.warn(
      `[iap] No RevenueCat API key for platform=${platform}. Set VITE_REVENUECAT_${platform.toUpperCase()}_API_KEY.`,
    );
    return;
  }

  try {
    const { Purchases, LOG_LEVEL } = await loadPurchases();
    if (!configured) {
      await Purchases.setLogLevel({
        level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.WARN,
      });
      await Purchases.configure({ apiKey, appUserID: userId });
      configured = true;
      currentUserId = userId;
    } else if (currentUserId !== userId) {
      // Same session, different user (rare — but happens if we sign out
      // and sign back in as somebody else without a full app relaunch).
      await Purchases.logIn({ appUserID: userId });
      currentUserId = userId;
    }
  } catch (err) {
    console.error('[iap] initIap failed:', err);
  }
}

/**
 * Fetch the current offering. Returns null if no offering is configured
 * in the RevenueCat dashboard yet, if we're on the web, or if the fetch
 * fails. The caller (Paywall.tsx) shows an empty-state / error banner in
 * those cases and gives the user a "Restore purchases" fallback.
 */
export async function getOfferings(): Promise<PurchasesOffering | null> {
  if (!isNativeApp()) return null;
  try {
    const { Purchases } = await loadPurchases();
    const { current } = await Purchases.getOfferings();
    return current ?? null;
  } catch (err) {
    console.error('[iap] getOfferings failed:', err);
    return null;
  }
}

/**
 * Attempt an in-app purchase. Shows the native purchase sheet and blocks
 * until the user confirms, cancels, or Apple/Google errors out. Callers
 * should be prepared to catch the "user cancelled" case and just clear
 * their loading state — that's a normal outcome, not an error.
 */
export async function purchasePackage(pkg: PurchasesPackage): Promise<CustomerInfo> {
  if (!isNativeApp()) {
    throw new Error('In-app purchase is only available in the mobile app.');
  }
  const { Purchases } = await loadPurchases();
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  return result.customerInfo;
}

/**
 * Restore purchases. Every subscription app MUST expose this control
 * (Apple Guideline 3.1.1): a user who re-installs the app or switches
 * phones needs a way to reclaim an entitlement they already paid for
 * without buying it again.
 */
export async function restorePurchases(): Promise<CustomerInfo> {
  if (!isNativeApp()) {
    throw new Error('Restore purchases is only available in the mobile app.');
  }
  const { Purchases } = await loadPurchases();
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}

/**
 * Client-side entitlement check. Uses whatever CustomerInfo we've most
 * recently seen from the SDK — pass the result of `purchasePackage`,
 * `restorePurchases`, or a fresh `getCustomerInfo()` call. Prefer the
 * server-side flag on `organisations.subscription_status` for gating
 * app access (the webhook writes that after Apple confirms the receipt);
 * this helper is just for optimistic UI right after a purchase.
 *
 * The entitlement identifier "pro" is configured in the RC dashboard →
 * Entitlements. All six products (small team / team / site × monthly /
 * annual) are attached to it.
 */
export function hasProEntitlement(ci: CustomerInfo | null | undefined): boolean {
  if (!ci) return false;
  const ent = ci.entitlements?.active?.['pro'];
  return Boolean(ent?.isActive);
}

/** Fetch the currently-cached CustomerInfo without triggering a receipt refresh. */
export async function getCurrentCustomerInfo(): Promise<CustomerInfo | null> {
  if (!isNativeApp()) return null;
  try {
    const { Purchases } = await loadPurchases();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (err) {
    console.error('[iap] getCurrentCustomerInfo failed:', err);
    return null;
  }
}

/** Clear the RevenueCat session on sign-out. */
export async function logoutIap(): Promise<void> {
  if (!isNativeApp() || !configured) return;
  try {
    const { Purchases } = await loadPurchases();
    await Purchases.logOut();
    currentUserId = null;
  } catch (err) {
    // If the SDK is in anonymous mode this throws — safe to swallow.
    console.warn('[iap] logoutIap: already anonymous or not configured', err);
  }
}

/**
 * Maps a RevenueCat product identifier back to our internal tier + cycle.
 * Used by the Paywall to render the right label / price on each package
 * card even before the RC offering finishes loading.
 *
 * Keep in sync with the PRODUCT_TO_TIER map in the revenuecat-webhook
 * Edge Function — same product IDs, same three tiers.
 */
export function productToTier(
  productId: string,
): { tier: 'tier1' | 'tier2' | 'tier3'; cycle: 'monthly' | 'annual' } | null {
  const map: Record<string, { tier: 'tier1' | 'tier2' | 'tier3'; cycle: 'monthly' | 'annual' }> = {
    'com.safeandsorted.app.smallteam.monthly': { tier: 'tier1', cycle: 'monthly' },
    'com.safeandsorted.app.smallteam.annual':  { tier: 'tier1', cycle: 'annual'  },
    'com.safeandsorted.app.team.monthly':      { tier: 'tier2', cycle: 'monthly' },
    'com.safeandsorted.app.team.annual':       { tier: 'tier2', cycle: 'annual'  },
    'com.safeandsorted.app.site.monthly':      { tier: 'tier3', cycle: 'monthly' },
    'com.safeandsorted.app.site.annual':       { tier: 'tier3', cycle: 'annual'  },
  };
  // Google Play product ids arrive as "subscriptionId:basePlanId".
  return map[productId.split(':')[0]] ?? null;
}
